'use client'

import * as React from 'react'
import { useState, useMemo } from 'react'
import { questions, catMeta } from './types'

export interface PresenceDetailResult {
  id: string
  type: string
  answers: Record<string, number>
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface PresenceHistoryDetailProps {
  result: PresenceDetailResult
  onSaveNotes: (notes: string) => Promise<void>
  onStartNew?: () => void
}

function calculateScores(answers: Record<string, number>) {
  const catScores = {
    immersion: { total: 0, count: 0 },
    attunement: { total: 0, count: 0 },
    emotional: { total: 0, count: 0 },
    focus: { total: 0, count: 0 },
  }

  questions.forEach((q, i) => {
    const answer = answers[i.toString()]
    if (answer !== null && answer !== undefined) {
      const scoreVal = q.reverse ? 8 - answer : answer
      const catData = catScores[q.cat as keyof typeof catScores]
      if (catData) {
        catData.total += scoreVal
        catData.count += 1
      }
    }
  })

  const scores = {
    immersion: catScores.immersion.count > 0 ? catScores.immersion.total / catScores.immersion.count : 0,
    attunement: catScores.attunement.count > 0 ? catScores.attunement.total / catScores.attunement.count : 0,
    emotional: catScores.emotional.count > 0 ? catScores.emotional.total / catScores.emotional.count : 0,
    focus: catScores.focus.count > 0 ? catScores.focus.total / catScores.focus.count : 0,
  }

  const validScores = Object.values(scores).filter(s => s > 0)
  const overallScore = validScores.length > 0 ? validScores.reduce((a, b) => a + b, 0) / validScores.length : 0

  return { scores, overallScore }
}

function getInterpretation(score: number) {
  if (score >= 5.5) return { level: 'high', color: '#4A7C7C', text: 'Strong presence in this area.' }
  if (score >= 4) return { level: 'mid', color: '#D4A574', text: 'Moderate presence — some room for attention.' }
  return { level: 'low', color: '#C4938A', text: 'This area may need more attention.' }
}

export function PresenceHistoryDetail({
  result,
  onSaveNotes,
  onStartNew,
}: PresenceHistoryDetailProps) {
  const [notes, setNotes] = useState(result.notes || '')
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [notesSaved, setNotesSaved] = useState(false)

  const { scores, overallScore } = useMemo(() => {
    return calculateScores(result.answers)
  }, [result])

  const handleSaveNotes = async () => {
    setIsSavingNotes(true)
    try {
      await onSaveNotes(notes.trim())
      setNotesSaved(true)
    } catch (error) {
      console.error('Failed to save notes:', error)
    } finally {
      setIsSavingNotes(false)
    }
  }

  return (
    <div className="max-w-[600px] mx-auto">
      {/* Overall Score */}
      <div className="bg-white rounded-2xl p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6">
        <div className="text-center mb-8 pb-8 border-b border-[#E8E0D8]">
          <div className="text-5xl font-serif text-[#4A7C7C] mb-2">
            {overallScore.toFixed(1)}
          </div>
          <p className="text-sm text-[#7A7A7A]">
            Overall Presence Score (out of 7)
          </p>
        </div>

        {/* Category Scores */}
        <div className="space-y-6">
          {(['immersion', 'attunement', 'emotional', 'focus'] as const).map((cat) => {
            const score = scores[cat]
            const meta = catMeta[cat]
            const interp = getInterpretation(score)
            const percent = (score / 7) * 100

            return (
              <div key={cat}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{meta?.emoji}</span>
                    <span className="font-medium text-[#2D2D2D]">{meta?.name}</span>
                  </div>
                  <span className="font-semibold text-[#2D2D2D]">{score.toFixed(1)}</span>
                </div>
                <div className="h-2 bg-[#E8E0D8] rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${percent}%`, backgroundColor: interp.color }}
                  />
                </div>
                <p className="text-xs text-[#7A7A7A]">{interp.text}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Interpretation */}
      <div className="bg-[#E8F2F2] rounded-2xl p-6 mb-6">
        <h3 className="font-serif text-lg text-[#2E5B5B] mb-3">
          What this might mean
        </h3>
        <div className="text-sm text-[#2E5B5B] leading-relaxed space-y-3">
          {overallScore >= 5.5 ? (
            <p>
              You were deeply present across all dimensions — grounded in yourself while fully available to your client. Notice what conditions supported this.
            </p>
          ) : overallScore >= 4 ? (
            <p>
              Your presence was steady across most dimensions. The value of this tool is in tracking patterns over time — notice how your presence shifts across different sessions.
            </p>
          ) : (
            <p>
              These scores are information, not a verdict. Presence naturally fluctuates — it varies by session, by day, by what's happening in your life.
            </p>
          )}

          {scores.focus < 4 && (
            <p>
              Focus and sustained attention scored lower. Mind-wandering is normal — the question is what's pulling your attention away.
            </p>
          )}

          {scores.emotional < 4 && (
            <p>
              Emotional availability was limited. You may have felt a barrier between you and your client, or noticed your outward responses not matching your inner experience.
            </p>
          )}

          {scores.attunement < 4 && (
            <p>
              Attunement scored lower this session. Consider what might have made it harder to sense into your client's experience.
            </p>
          )}

          {scores.immersion < 4 && (
            <p>
              Immersion was limited. Something may have been keeping you at the edges of the session rather than fully entering it.
            </p>
          )}
        </div>
      </div>

      {/* Questions to Sit With */}
      <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E8E0D8]">
        <h3 className="font-medium text-[#2D2D2D] mb-3">
          Questions to sit with
        </h3>
        <ul className="text-sm text-[#7A7A7A] space-y-2">
          <li>• What was going on in your life before this session?</li>
          {scores.focus < 4 && (
            <li>• If your mind wandered, what was it drawn to?</li>
          )}
          {scores.emotional < 4 && (
            <li>• Was there something that felt hard to express in the room?</li>
          )}
          {scores.attunement < 4 && (
            <li>• What might have made it harder to sense into your client's world?</li>
          )}
          {scores.immersion < 4 && (
            <li>• What might have been keeping you at the edges of the session?</li>
          )}
          {scores.focus >= 4 && scores.emotional >= 4 && scores.attunement >= 4 && scores.immersion >= 4 && (
            <li>• What helped you be so present today?</li>
          )}
          <li>• What would help you arrive more fully next time?</li>
        </ul>
      </div>

      {/* Notes Section */}
      <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E8E0D8]">
        <h3 className="font-medium text-[#2D2D2D] mb-2">
          Your Reflections
        </h3>
        <p className="text-sm text-[#7A7A7A] mb-4">
          Capture any thoughts or insights while they're fresh. This is just for you.
        </p>
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value)
            setNotesSaved(false)
          }}
          placeholder="What stood out to you about this session? Any patterns you're noticing?"
          className="w-full p-4 text-sm border border-[#E8E0D8] rounded-xl resize-none focus:border-[#4A7C7C] focus:ring-1 focus:ring-[#4A7C7C]/20 outline-none transition-colors"
          rows={4}
        />
        {notes.trim() && (
          <div className="mt-3 flex items-center justify-end gap-3">
            {notesSaved && (
              <span className="text-xs text-[#4A7C7C] flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}
            <button
              onClick={handleSaveNotes}
              disabled={isSavingNotes || notesSaved}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                notesSaved
                  ? 'bg-[#E8F2F2] text-[#4A7C7C] cursor-default'
                  : 'bg-[#4A7C7C] text-white hover:bg-[#2E5B5B]'
              } disabled:opacity-50`}
            >
              {isSavingNotes ? 'Saving...' : notesSaved ? 'Saved' : 'Save Notes'}
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="text-center mt-8">
        {onStartNew && (
          <button
            onClick={onStartNew}
            className="text-sm text-[#7A7A7A] hover:text-[#4A7C7C] transition-colors"
          >
            Start a new self-reflection
          </button>
        )}
        <p className="text-[10px] text-[#bbb] mt-6">
          Based on the Therapeutic Presence Inventory by Dr. Shari Geller
        </p>
      </div>
    </div>
  )
}
